import React, { useRef, useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Video } from '@fiverr-private/media';
import { I18n } from '@fiverr-private/i18n-react';
import { usePrefersReducedMotion } from '../../../../shared/hooks/usePrefersReducedMotion';
import SafeComponent from '../../shared/SafeComponent';
import { videoEventsHandler } from '../../utils/videoEventsHandler';
import { SEARCH_TYPING_PHRASE_KEYS, VIDEO_POSTERS, VIDEO_SOURCES } from '../../constants';
import AnimatedSearchBar from '../AnimatedSearchBar';
import VideoControlButton from '../VideoControlButton';
import PopularServices from '../PopularServices';
import TrustedBySection from '../TrustedBySection';

import styles from './styles.module.scss';

interface Props {
    shouldLoadVideo?: boolean;
}

const VideoHeroBanner = ({ shouldLoadVideo = true }: Props) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const prefersReducedMotion = usePrefersReducedMotion();

    const handleVideoButtonClick = () => {
        if (!videoRef.current) {
            return;
        }

        const params = { component_name: 'VideoHeroBanner' };

        if (!isPlaying) {
            videoRef.current.play();
            videoEventsHandler({ actionType: 'Play', params });
            setIsPlaying(true);
        } else {
            videoRef.current.pause();
            videoEventsHandler({ actionType: 'Pause', params });
            setIsPlaying(false);
        }
    };

    return (
        <Stack width="100%" position="relative" height={{ default: '572px', sm: '680px' }}>
            <Container
                className="wrapper"
                width="100%"
                position="absolute"
                top="0px"
                right="0px"
                left="0px"
                bottom="0px"
            >
                {prefersReducedMotion && (
                    <Container
                        style={{ background: `url(${VIDEO_POSTERS.desktop}) top #000 no-repeat` }}
                        className={styles.placeholder}
                        aria-hidden
                    />
                )}
                {!prefersReducedMotion && (
                    <Video
                        width="100%"
                        height="100%"
                        autoPlay
                        playsInline
                        preload="auto"
                        poster={VIDEO_POSTERS.desktop}
                        muted
                        loop={true}
                        controls={false}
                        ref={videoRef}
                        sources={shouldLoadVideo ? [VIDEO_SOURCES.desktop] : []}
                        aria-hidden
                    />
                )}
            </Container>
            <Stack
                width="100%"
                height="100%"
                display="flex"
                direction="column"
                alignItems="flexStart"
                zIndex="2"
                margin="auto"
                maxWidth="1400px"
                className={styles.container}
            >
                <Stack
                    height="100%"
                    display="flex"
                    direction="column"
                    alignItems="flexStart"
                    maxWidth={{
                        default: '100%',
                        sm: '512px',
                        md: '705px',
                        lg: '100%',
                    }}
                    zIndex="2"
                    overflow="visible"
                    gap="7"
                >
                    <Container>
                        <Typography
                            color="white"
                            style={{ fontWeight: 280 }}
                            marginBottom={{ default: 'auto', sm: '0' }}
                            className={styles.title}
                            letterSpacing="-0.06em"
                            as="h1"
                        >
                            <I18n k="logged_out_homepage.video_top_banner.title" />
                        </Typography>
                    </Container>
                    <Container width="100%" maxWidth="100%">
                        <AnimatedSearchBar
                            placeholderKey="logged_out_homepage.hero.search"
                            phraseKeys={SEARCH_TYPING_PHRASE_KEYS}
                            source="main_banner"
                            submitButtonType="button_inside"
                            theme="dark"
                        />
                    </Container>
                    <PopularServices />
                </Stack>
                <Stack marginTop={{ default: '5', sm: 'auto' }} width="100%" alignItems="center">
                    <TrustedBySection />
                    <VideoControlButton videoRef={videoRef} isPlaying={isPlaying} onToggle={handleVideoButtonClick} />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default SafeComponent(VideoHeroBanner);
